import React, {
  MouseEventHandler,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import cx from 'classnames';

import { useId } from '~shared/hooks';

import {
  CLOSE_MENU,
  GO_TO_ITEM,
  REGISTER_ITEM,
  UNREGISTER_ITEM,
} from './actions';
import { RenderProps } from './hooks/useMenuItem';
import { MenuItemDataRef, useMenuContext } from './MenuContext';
import { disposables } from './utils/disposables';
import { Focus } from './utils/getIndex';

interface ButtonRenderProps extends RenderProps {
  onClick: MouseEventHandler<HTMLElement>;
}

export interface MenuItemRenderPropArgs {
  isActive: boolean;
  props?: ButtonRenderProps;
}

export interface MenuItemProps {
  children: ({ isActive, props }: MenuItemRenderPropArgs) => ReactElement;
  disabled?: boolean;
  dismissOnClick?: boolean;
  onClick?: (event: MouseEvent) => void;
}

/**
 * A renderless component for menu items that manages a11y attributes and
 * event handlers. Use the render props on child elements/components:
 *
 * - `isActive` can be used to conditionally style the active menu item
 * - `props` contains the appropriate a11y attributes and event handlers
 *
 * If a given menu item should be disabled, use the `disabled` boolean prop on
 * this component and it will pass the appropriate attribute along to the child
 * button — specifically, `aria-disabled` is used in place of the more
 * "heavy-handed", `disabled` attribute.
 *
 * @see https://www.w3.org/TR/wai-aria-1.1/#menuitem
 */
export function MenuItem(props: MenuItemProps) {
  const {
    children,
    disabled = false,
    dismissOnClick = true,
    onClick,
    ...passthroughProps
  } = props;
  const [state, dispatch] = useMenuContext('MenuItem');
  const id = `menu-item-${useId()}`;
  const isActive =
    state.activeItemIndex !== null
      ? state.items[state.activeItemIndex].id === id
      : false;

  useEffect(() => {
    if (!state.isOpen) return;
    if (!isActive) return;

    const d = disposables();

    d.nextFrame(() =>
      document.getElementById(id)?.scrollIntoView?.({ block: 'nearest' })
    );

    return d.dispose;
  }, [state.isOpen, isActive, id]);

  const ref = useRef<MenuItemDataRef['current']>({ disabled });

  /** Add disabled state to item data. */
  useEffect(() => {
    ref.current.disabled = disabled;
  }, [ref, disabled]);

  /** Add text content to item data. */
  useEffect(() => {
    ref.current.text = document.getElementById(id)?.textContent?.toLowerCase();
  }, [ref, id]);

  /** Add (or remove) item data to state items list. */
  useEffect(() => {
    dispatch({ type: REGISTER_ITEM, id, dataRef: ref });
    return () => dispatch({ type: UNREGISTER_ITEM, id });
  }, [dispatch, ref, id]);

  const handleClick = useCallback(
    (event) => {
      if (disabled) return event.preventDefault();

      if (dismissOnClick) dispatch({ type: CLOSE_MENU });
      disposables().nextFrame(() =>
        state.buttonRef.current?.focus({ preventScroll: true })
      );

      if (onClick) return onClick(event);
    },
    [disabled, dismissOnClick, dispatch, onClick, state.buttonRef]
  );

  const handleFocus = useCallback(() => {
    if (disabled) return dispatch({ type: GO_TO_ITEM, focus: Focus.NOTHING });
    dispatch({ type: GO_TO_ITEM, focus: Focus.SPECIFIC, id });
  }, [disabled, dispatch, id]);

  const handleMove = useCallback(() => {
    if (disabled) return;
    if (isActive) return;
    dispatch({ type: GO_TO_ITEM, focus: Focus.SPECIFIC, id });
  }, [disabled, isActive, dispatch, id]);

  const handleLeave = useCallback(() => {
    if (disabled) return;
    if (!isActive) return;
    dispatch({ type: GO_TO_ITEM, focus: Focus.NOTHING });
  }, [disabled, isActive, dispatch]);

  const itemProps = {
    id,
    role: 'menuitem',
    tabIndex: disabled === true ? undefined : -1,
    'aria-disabled': disabled === true ? true : undefined,
    disabled: undefined,
    onClick: handleClick,
    onFocus: handleFocus,
    onPointerMove: handleMove,
    onMouseMove: handleMove,
    onPointerLeave: handleLeave,
    onMouseLeave: handleLeave,
  };

  return children({
    isActive,
    props: { ...passthroughProps, ...itemProps },
  });
}

type StyledProps = Omit<MenuItemProps, 'children'> & {
  children: React.ReactNode;
  isDestructive?: boolean;
};

export function StyledMenuItem({
  children,
  isDestructive,
  ...restProps
}: StyledProps) {
  const getLinkClass = (isActive = false, isDisabled = false) =>
    cx(
      'flex w-full px-4 py-2 whitespace-no-wrap',
      'font-sans text-base leading-8 text-left border-0 rounded-1',
      isActive ? 'bg-cyan-50' : 'bg-white',
      isDisabled
        ? 'text-grey-600 cursor-disabled'
        : isDestructive
        ? 'text-red-800 cursor-pointer'
        : 'text-blue-900 cursor-pointer'
    );

  return (
    <MenuItem {...restProps}>
      {({ isActive, props }) => (
        <div className="px-2 py-1">
          <button
            className={getLinkClass(isActive, props['aria-disabled'])}
            type="button"
            {...props}
          >
            {children}
          </button>
        </div>
      )}
    </MenuItem>
  );
}
